import { useNavigate, useParams } from "react-router-dom";
import useDocumentTitle from "../../util/useDocumentTitle";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { User, UserGroup } from "../../common/User";
import { useMemo, useState } from "react";
import formData from "./formValidation/formData";
import {
  useGetTrainingLocationsQuery,
  useGetTrainingsQuery,
} from "../../redux/api/training";
import {
  useCreateTrainingOrderMutation,
  useGetTrainingOrderQuery,
  useLazyGetTrainingOrderHistoryQuery,
  useMarkTrainingOrderDeletedMutation,
  useUpdateTrainingOrderMutation,
} from "../../redux/api/trainingOrder";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetVesselsQuery } from "../../redux/api/vessel";
import { useGetUsersByGroupQuery } from "../../redux/api/user";
import initialValues from "./formValidation/initialValues";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  OutlinedInput,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Formik, useFormikContext, Form } from "formik";
import { usePrompt } from "../../util/usePrompt";
import validationSchema from "./formValidation/validationSchema";
import { toast } from "react-toastify";
import {
  Delete,
  ArrowBack,
  CheckCircle,
  Call,
  Email,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import AutocompleteField from "../../common/FormFields/AutocompleteField";
import InputField from "../../common/FormFields/InputField";
import ChecklistDisplay from "../jobOrder/ChecklistDisplay";
import TextAreaField from "../../common/FormFields/TextAreaField";
import EmailModal from "../email/EmailModal";
import { EmailType } from "../../common/EmailDetails";
import {
  Gender,
  GenderSymbol,
  MarinerProfile,
} from "../../common/MarinerProfile";
import { formatDateString } from "../../common/FormatDateString";
import SelectField from "../../common/FormFields/SelectField";
import convertDateToString from "../../util/ConvertDateToString";
import {
  SignerType,
  SignerTypeLabel,
  TravelDocumentOperationType,
} from "../../common/TravelDocument";
import {
  Training,
  TrainingLocation,
  TrainingOrder,
  TrainingOrderFieldName,
  TrainingOrderFieldNameLabel,
} from "../../common/Training";
import {
  JobRating,
  DisabledJobRatings,
  JobRatingLabel,
} from "../../common/JobOrder";
import MarinerAutocompleteField from "../../common/FormFields/MarinerAutocompleteField";
import TrainingTravelDocuments from "./TrainingTravelDocuments";
import JobRequirementsDisplay from "../jobBoard/JobRequirementsDisplay";
import { colorFromName } from '../../common/ColorFromName';

const TrainingOrderForm = () => {
  useDocumentTitle("Training Order Details");
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const isMariner = (user?.userGroup as UserGroup) === UserGroup.MARINERS;
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currDeletedNote, setCurrDeletedNote] = useState("");
  const [showTrainingOrderHistory, setShowTrainingOrderHistory] = useState(false);

  const { formId, formField } = formData;
  const {
    createdDate,
    dueDate,
    startDate,
    endDate,
    vessel,
    assignee,
    mariner,
    marinerLeavingFrom,
    marinerDestination,
    marinerTravelTimestamp,
    marinerAccomodation,
    marinerPickupDate,
    marinerDropoffDate,
    comments,
    marinerTwicTsaNum,
    marinerDateOfBirth,
    checklistRoot,
    travelDocuments,
    rating,
    flightNotice,
    trainingLocation,
    training,
    daysToBePaid,
    note,
  } = formField;

  const { id } = useParams();

  const {
    data: trainingOrderFromApi,
    isLoading,
    isFetching,
    refetch,
  } = useGetTrainingOrderQuery(
    id !== undefined && id !== "new" ? id : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [getTrainingOrderHistory, trainingOrderHistory] = useLazyGetTrainingOrderHistoryQuery();

  // may need dispatch search params and dispatch query

  const { data: trainings } = useGetTrainingsQuery();
  const { data: locations } = useGetTrainingLocationsQuery();
  const { data: vessels } = useGetVesselsQuery({});
  const { data: specialists } = useGetUsersByGroupQuery(
    UserGroup.CREWING_SPECIALISTS
  );
  const { data: managers } = useGetUsersByGroupQuery(
    UserGroup.CREWING_MANAGERS
  );

  const { data: evalSpecialists } = useGetUsersByGroupQuery(
    UserGroup.CREWING_EVAL_SPECIALISTS
  );

  const assignees = useMemo(() => {
    return [...(specialists || []), ...(managers || []), ...(evalSpecialists || [])];
  }, [specialists, managers, evalSpecialists]);

  // more APIs go here to create/update etc.
  const [createTrainingOrder] = useCreateTrainingOrderMutation();
  const [updateTrainingOrder] = useUpdateTrainingOrderMutation();
  const [markTrainingOrderDeleted] = useMarkTrainingOrderDeletedMutation();

  // let marinerRegistrationStatus = "";
  // let marinerEmail = false;

  let getInitialValues = () => {
    if (!isLoading && !isFetching && id !== undefined && id !== "new") {
      let res: any = JSON.parse(JSON.stringify(trainingOrderFromApi));
      Object.keys(res).forEach((key) => {
        res[key] = res[key] === null ? "" : res[key];
      });
      // marinerRegistrationStatus = res["mariner"] && res["mariner"]["cognitoUser"]
      //     ? res["mariner"]["cognitoUser"]["status"] : "";
      // marinerEmail = res["mariner"] && res["mariner"]["email"];
      return {
        [createdDate.name]:
          res["createdDate"] !== undefined && res["createdDate"] !== ""
            ? convertDateToString(new Date(res["createdDate"]))
            : res["createdDate"],
        [dueDate.name]:
          res["dueDate"] !== undefined && res["dueDate"] !== ""
            ? convertDateToString(new Date(res["dueDate"]))
            : res["dueDate"],
        [startDate.name]:
          res["startDate"] !== undefined && res["startDate"] !== ""
            ? convertDateToString(new Date(res["startDate"]))
            : res["startDate"],
        [endDate.name]:
          res["endDate"] !== undefined && res["endDate"] !== ""
            ? convertDateToString(new Date(res["endDate"]))
            : res["endDate"],
        [vessel.name]: res["vessel"],
        [assignee.name]: res["assignee"],
        [mariner.name]: res["mariner"],
        [marinerLeavingFrom.name]: res["marinerLeavingFrom"],
        [marinerDestination.name]: res["marinerDestination"],
        [marinerTravelTimestamp.name]: res["marinerTravelTimestamp"],
        [marinerAccomodation.name]: res["marinerAccomodation"],
        [marinerPickupDate.name]:
          res["marinerPickupDate"] !== undefined &&
          res["marinerPickupDate"] !== ""
            ? convertDateToString(new Date(res["marinerPickupDate"]))
            : res["marinerPickupDate"],
        [marinerDropoffDate.name]:
          res["marinerDropoffDate"] !== undefined &&
          res["marinerDropoffDate"] !== ""
            ? convertDateToString(new Date(res["marinerDropoffDate"]))
            : res["marinerDropoffDate"],
        [comments.name]: res["comments"],
        [marinerTwicTsaNum.name]: res["mariner"]["twicTsaNum"] || "",
        [marinerDateOfBirth.name]: res["mariner"]["dateOfBirth"]
          ? convertDateToString(new Date(res["mariner"]["dateOfBirth"]))
          : "",
        [checklistRoot.name]: res["checklistRoot"],
        [travelDocuments.name]: res["travelDocuments"],
        [rating.name]: res["rating"],
        [flightNotice.name]: res["flightNotice"],
        [trainingLocation.name]: res["trainingLocation"],
        [training.name]: res["training"],
        [daysToBePaid.name]: res["daysToBePaid"],
        [note.name]: res["note"] || "",
      };
    } else {
      return initialValues;
    }
  };

  // handleSignerCallback to set DOB, TwicTsaNum
  const handleSignerCallback = (
    signerType: SignerType,
    mariner: MarinerProfile | undefined,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(
      `${SignerTypeLabel[signerType as SignerType]}DateOfBirth`,
      mariner?.dateOfBirth
        ? convertDateToString(new Date(mariner.dateOfBirth))
        : ""
    );
    setFieldValue(
      `${SignerTypeLabel[signerType as SignerType]}TwicTsaNum`,
      mariner?.twicTsaNum ? mariner.twicTsaNum : ""
    );
  };

  // handleTravelDocumentCallback
  const handleTravelDocumentCallback = (
    signer: SignerType,
    operationType: TravelDocumentOperationType
  ) => {
    if (operationType !== TravelDocumentOperationType.LOAD) {
      toast.success(
        <p>
          {SignerTypeLabel[signer as SignerType]} travel document{" "}
          {operationType.toLowerCase()} successful.
        </p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    }
  };

  // handleEmailCallBack
  const handleEmailCallBack = () => {
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  // buildUpdatedJobOrder
  const buildUpdatedTrainingOrder = (values: any) => {
    const trainingOrder: TrainingOrder = {
      createdDate: values.createdDate,
      dueDate: values.dueDate,
      startDate: values.startDate,
      endDate: values.endDate,
      vessel: values.vessel?.id ? values.vessel : undefined,
      assigneeId: values.assignee?.id || undefined,
      mariner: values.mariner?.id ? values.mariner : undefined,
      marinerLeavingFrom: values.marinerLeavingFrom
        ? values.marinerLeavingFrom
        : undefined,
      marinerDestination: values.marinerDestination
        ? values.marinerDestination
        : undefined,
      marinerTravelTimestamp: values.marinerTravelTimestamp
        ? values.marinerTravelTimestamp
        : undefined,
      marinerAccomodation: values.marinerAccomodation
        ? values.marinerAccomodation
        : undefined,
      marinerPickupDate: values.marinerPickupDate
        ? values.marinerPickupDate
        : undefined,
      marinerDropoffDate: values.marinerDropoffDate
        ? values.marinerDropoffDate
        : undefined,
      marinerNotes: values.marinerNotes ? values.marinerNotes : undefined,
      checklistRoot: values.checklistRoot?.id
        ? values.checklistRoot
        : undefined,
      travelDocuments:
        values.travelDocuments.length === 0
          ? undefined
          : values.travelDocuments,
      rating: values.rating,
      flightNotice:
        values.flightNotice !== undefined ? values.flightNotice : undefined,
      trainingLocation: values.trainingLocation || undefined,
      training: values.training || undefined,
      daysToBePaid: values.daysToBePaid || undefined,
      note: values.note || undefined,
      status: trainingOrderFromApi?.status!,

      // TODO add completionDate to other instantiations
      completionDate: values.completionDate,
      expirationDate: values.expirationDate,
      comments: values.comments,
    };
    return trainingOrder;
  };

  // _submitForm
  async function _submitForm(values: any, actions: any) {
    let trainingOrderId = Number(id);
    if (!!id && id !== "new") {
      const updateResult = await updateTrainingOrder({
        trainingOrderId: id,
        trainingOrder: buildUpdatedTrainingOrder(values),
      });
      if ("error" in updateResult) {
        actions.setSubmitting(false);
        return;
      }
    } else {
      const createResult = await createTrainingOrder(
        buildUpdatedTrainingOrder(values)
      );
      if ("error" in createResult) {
        actions.setSubmitting(false);
        return;
      }
      trainingOrderId = createResult.data.id as number;
    }
    actions.setSubmitting(false);
    !id || id === "new"
      ? navigate(`/trainingOrders/${trainingOrderId}`)
      : refetch();
    toast.success(
      <p>
        Training Order {!!id && id !== "new" ? "updated" : "created"}{" "}
        successfully.
      </p>,
      {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      }
    );
  }

  function _handleSubmit(values: any, actions: any) {
    if (!id || id === "new") {
      actions.resetForm({ values });
      actions.setSubmitting(true);
    }
    _submitForm(values, actions);
  }

  const IsFormDirty = () => {
    const { dirty } = useFormikContext();
    usePrompt("Changes will not be saved, would you like to leave?", dirty);
    return <></>;
  };

  const handleOpen = () => {
    setConfirmDelete(true);
  };

  const handleClose = () => {
    setConfirmDelete(false);
  };

  const handleTrainingOrderDelete = async () => {
    if (!id || id === "new") return;
    if (!currDeletedNote) {
      toast.error(<p>Note is required for deleted Training Orders.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    // TODO create api in frontend/redux for deleting training Orders
    const response = await markTrainingOrderDeleted({
      trainingOrderId: id,
      note: currDeletedNote,
    });
    if (!("error" in response)) {
      toast.success(<p>Training Order successfully marked for deletion</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    handleClose();
    refetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          _handleSubmit(values, actions);
        }}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form id={formId}>
            <IsFormDirty />
            <div className="page-header-between job-header-width align-items-center">
              <div className="flex gc-3">
                <h1>Training Order</h1>
                <div className="flex gc-3">
                  {!isViewOnly && (
                    <Button
                      variant="contained"
                      className="blue-button-small ht-4px"
                      type="submit"
                      form="trainingOrderForm"
                      disabled={isSubmitting}
                    >
                      {" "}
                      {isSubmitting === true ? (
                        <CircularProgress className="flex align-items-center color-white wd-2px" />
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  )}
                  {!isViewOnly &&
                    id &&
                    id !== "new" &&
                    !trainingOrderFromApi?.checklistRoot?.completed && (
                      <Button
                        className="grey-link-button"
                        onClick={() => handleOpen()}
                      >
                        <Delete />
                        Delete Training Order
                      </Button>
                    )}
                </div>
              </div>
              <div className="flex align-items-center justify-content-end">
                <Button
                  className="blue-link-button"
                  onClick={() => navigate(-1)}
                >
                  {" "}
                  <ArrowBack />
                  Back
                </Button>
              </div>

              <Dialog
                open={confirmDelete}
                onClose={handleClose}
                aria-labelledby="modal-title"
              >
                <Typography variant="h3" id="modal-title">
                  Delete Training Order
                </Typography>
                <p className="delete-paragraph">
                  Are you sure you would like to delete this Training Order?
                </p>
                <label>
                  <span>Note:</span>
                  <textarea
                    id="currDeletedNote"
                    value={currDeletedNote}
                    onChange={(e) => setCurrDeletedNote(e.target.value)}
                    maxLength={1000}
                  />
                </label>
                <div className="modal-footer">
                  <Button
                    className="outline-button-small"
                    onClick={handleTrainingOrderDelete}
                  >
                    Delete
                  </Button>
                  <Button className="blue-button-small" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </Dialog>
            </div>

            <main>
              <Paper className="order-header-wrap">
                <div className="order-header">
                  <label>
                    <span className="required">{assignee.label}</span>
                    <AutocompleteField
                      name={assignee.name}
                      data={assignees}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(assignee: User) => {
                        return assignee.name || assignee.email;
                      }}
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span className="required">{dueDate.label}</span>
                    <InputField
                      name={dueDate.name}
                      fullWidth
                      size="small"
                      type="date"
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span className="required">{vessel.label}</span>
                    <AutocompleteField
                      name={vessel.name}
                      data={!!vessels ? vessels.filter((v) => !v.disabled) : []}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(vessel: { name: any }) => {
                        return vessel?.name ? vessel.name : "";
                      }}
                      disabled={
                        isViewOnly || (id && id !== "new") ? true : false
                      }
                    />
                  </label>
                  <label>
                    <span className="required">{trainingLocation.label}</span>
                    <AutocompleteField
                      name={trainingLocation.name}
                      data={locations}
                      getOptionLabel={(location: TrainingLocation) => {
                        return location.name || "";
                      }}
                      setFieldValue={setFieldValue}
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span className="required">{rating.label}</span>
                    <AutocompleteField
                      name={rating.name}
                      data={Object.keys(JobRating).filter(
                        (rating) =>
                          !DisabledJobRatings.has(
                            JobRating[rating as JobRating]
                          )
                      )}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(option: string) =>
                        JobRatingLabel[
                          JobRating[option as keyof typeof JobRating]
                        ]
                      }
                      isOptionEqualToValue={(opt: string, val: string) =>
                        opt === val
                      }
                      disabled={
                        isViewOnly || (id && id !== "new") ? true : false
                      }
                    />
                  </label>
                  {!!id && id !== "new" && (
                    <label>
                      <span>Training Order ID</span>
                      <OutlinedInput value={`T-` + id} disabled={true} />
                    </label>
                  )}
                  <label>
                    <span>{createdDate.label}</span>
                    <InputField
                      name={createdDate.name}
                      fullWidth
                      size="small"
                      type="date"
                      disabled
                    />
                  </label>
                </div>
              </Paper>

              <div className="job-order">
                {/* TODO flesh out checklist */}
                <div className="checklist-section">
                  <div>
                    {!isViewOnly && values.checklistRoot && (
                      <ChecklistDisplay
                        root={values.checklistRoot}
                        userId={user?.username}
                      />
                    )}
                    {isViewOnly && values.checklistRoot && (
                      <JobRequirementsDisplay
                        root={values.checklistRoot}
                        userId={user?.username}
                      />
                    )}
                    {!values.checklistRoot && (
                      <Paper className="checklist placeholder">
                        <CheckCircle />
                        <p>
                          Save this Training Order to begin tracking progress
                        </p>
                      </Paper>
                    )}
                  </div>
                  <Paper className="deletion-note">
                    <label>
                      <h3>{note.label}</h3>
                      <TextAreaField
                        name={note.name}
                        size="small"
                        maxLength={1000}
                        disabled={isViewOnly}
                      />
                    </label>
                  </Paper>
                </div>

                <div className="order-details">
                  <Paper>
                    {/* TODO Make sure email button works */}
                    <div className="page-header">
                      <h3>Mariner</h3>
                      <div className="flex gc-3">
                        {!isViewOnly && (
                          <Tooltip title="Coming Soon">
                            <Button className="grey-link-button">
                              <Call />
                              Call
                            </Button>
                          </Tooltip>
                        )}
                        {!isViewOnly &&
                        !isLoading &&
                        !!trainingOrderFromApi &&
                        !!trainingOrderFromApi.mariner?.email ? (
                          <EmailModal
                            key={EmailType.EMAIL_TRAINING_CONTENT_TO_MARINER + "_mariner"}
                            vesselId={trainingOrderFromApi?.vessel?.id}
                            email={trainingOrderFromApi?.mariner?.email}
                            emailType={EmailType.EMAIL_TRAINING_CONTENT_TO_MARINER}
                            parentCallback={handleEmailCallBack}
                            trainingOrderId={!!id && !!+id ? +id : undefined}
                            action="Sent to Mariner"
                          >
                            <Button className="blue-link-button">
                              <Email />
                              Email
                            </Button>
                          </EmailModal>
                        ) : (
                          <Button className="grey-link-button">
                            <Email />
                            Email
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="form-narrow">
                      <label htmlFor="mariner">
                        <span>{mariner.label}</span>
                        <MarinerAutocompleteField
                          id="marinerAutocomplete"
                          name={mariner.name}
                          marinerRating={
                            values.rating ? values.rating : undefined
                          }
                          setFieldValue={setFieldValue}
                          onChangeCallback={(value: MarinerProfile) =>
                            handleSignerCallback(
                              SignerType.OnSigner,
                              value,
                              setFieldValue
                            )
                          }
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{training.label}</span>
                        <AutocompleteField
                          name={training.name}
                          data={trainings}
                          getOptionLabel={(training: Training) => {
                            return training.name || "";
                          }}
                          setFieldValue={setFieldValue}
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{startDate.label}</span>
                        <InputField
                          name={startDate.name}
                          fullWidth
                          size="small"
                          type="date"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{endDate.label}</span>
                        <InputField
                          name={endDate.name}
                          fullWidth
                          size="small"
                          type="date"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{daysToBePaid.label}</span>
                        <InputField
                          name={daysToBePaid.name}
                          fullWidth
                          size="small"
                          type="number"
                          disabled={isViewOnly}
                        />
                      </label>
                    </div>

                    <div className="page-header">
                      <h3>Mariner Travel</h3>
                      {!isViewOnly && (
                        <EmailModal
                          key={EmailType.EMAIL_TO_TRAVEL_AGENT + "_mariner"}
                          vesselId={trainingOrderFromApi?.vessel?.id}
                          trainingOrderId={!!id && !!+id ? +id : undefined}
                          action="Sent to Mariner Travel Agent"
                          emailData={{
                            vesselName: trainingOrderFromApi?.vessel
                              ?.name as string,
                            passenger:
                              trainingOrderFromApi?.mariner?.firstName +
                              " " +
                              trainingOrderFromApi?.mariner?.middleName +
                              " " +
                              trainingOrderFromApi?.mariner?.lastName,
                            gender:
                              GenderSymbol[
                                trainingOrderFromApi?.mariner?.gender as Gender
                              ],
                            dob:
                              trainingOrderFromApi?.mariner?.dateOfBirth ===
                                undefined ||
                              trainingOrderFromApi?.mariner?.dateOfBirth ===
                                null
                                ? "null"
                                : formatDateString(
                                    trainingOrderFromApi?.mariner?.dateOfBirth
                                  ),
                            twicTsaNum: trainingOrderFromApi?.mariner
                              ?.twicTsaNum as string,
                            from: trainingOrderFromApi?.marinerLeavingFrom as string,
                            to: trainingOrderFromApi?.marinerDestination as string,
                            date:
                              trainingOrderFromApi?.dueDate === undefined ||
                              trainingOrderFromApi?.dueDate === null
                                ? "null"
                                : formatDateString(
                                    trainingOrderFromApi?.dueDate
                                  ),
                            time: trainingOrderFromApi?.marinerTravelTimestamp as string,
                            hotelOrRental:
                              trainingOrderFromApi?.marinerAccomodation as string,
                            pickUpDate:
                              trainingOrderFromApi?.marinerPickupDate ===
                                undefined ||
                              trainingOrderFromApi?.marinerPickupDate === null
                                ? "null"
                                : formatDateString(
                                    trainingOrderFromApi?.marinerPickupDate
                                  ),
                            dropOffDate:
                              trainingOrderFromApi?.marinerDropoffDate ===
                                undefined ||
                              trainingOrderFromApi?.marinerDropoffDate === null
                                ? "null"
                                : formatDateString(
                                    trainingOrderFromApi?.marinerDropoffDate
                                  ),
                            phone: trainingOrderFromApi?.mariner
                              ?.phone as string,
                          }}
                          email={undefined}
                          emailType={EmailType.EMAIL_TO_TRAVEL_AGENT}
                          parentCallback={handleClose}
                        >
                          <Button className="blue-link-button">
                            <Email />
                            Email Travel Agent
                          </Button>
                        </EmailModal>
                      )}
                    </div>

                    <div className="form-narrow">
                      <label>
                        <span>{marinerLeavingFrom.label}</span>
                        <InputField
                          name={marinerLeavingFrom.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{marinerDestination.label}</span>
                        <InputField
                          name={marinerDestination.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{marinerAccomodation.label}</span>
                        <InputField
                          name={marinerAccomodation.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{marinerTwicTsaNum.label}</span>
                        <InputField
                          name={marinerTwicTsaNum.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled
                        />
                      </label>
                      <label>
                        <span>{marinerDateOfBirth.label}</span>
                        <InputField
                          name={marinerDateOfBirth.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled
                        />
                      </label>
                      <label>
                        <span>{marinerTravelTimestamp.label}</span>
                        <InputField
                          name={marinerTravelTimestamp.name}
                          fullWidth
                          size="small"
                          type="time"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{marinerPickupDate.label}</span>
                        <InputField
                          name={marinerPickupDate.name}
                          fullWidth
                          size="small"
                          type="date"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{marinerDropoffDate.label}</span>
                        <InputField
                          name={marinerDropoffDate.name}
                          fullWidth
                          size="small"
                          type="date"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{flightNotice.label}</span>
                        <SelectField
                          name={flightNotice.name}
                          data={[
                            { value: true, label: "Y" },
                            { value: false, label: "N" },
                          ]}
                          fullWidth
                          size="small"
                          disabled={isViewOnly}
                        />
                      </label>
                    </div>
                    {id !== undefined && !isNaN(+id) && (
                      <>
                        <h4>Travel Documents</h4>
                        <TrainingTravelDocuments
                          trainingOrderId={+id}
                          signer={SignerType.OnSigner}
                          emailTo={trainingOrderFromApi?.mariner?.email}
                          validEmail={
                            values.mariner?.email ===
                            trainingOrderFromApi?.mariner?.email
                          }
                          isViewOnly={isViewOnly}
                          parentCallback={handleTravelDocumentCallback}
                        />
                      </>
                    )}

                    <label className="mt-2">
                      <h3>{comments.label}</h3>
                      <TextAreaField
                        name={comments.name}
                        size="small"
                        disabled={isViewOnly}
                      />
                    </label>
                  </Paper>
                  {!isMariner && (
                    <Paper>
                      <div className="page-header justify-content-btwn">
                        <h3>Training Order History</h3>
                        {!showTrainingOrderHistory ? (
                          <div className="flex align-items-center justify-content-end">
                            <Button
                              className="blue-link-button"
                              onClick={() => {
                                if (id !== undefined && id !== "new") {
                                  getTrainingOrderHistory(id);
                                }
                                setShowTrainingOrderHistory(!showTrainingOrderHistory);
                              }}
                            >
                              {" "}
                              Expand
                              <ExpandMoreIcon />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex align-items-center justify-content-end">
                            <Button
                              className="blue-link-button"
                              onClick={() =>
                                setShowTrainingOrderHistory(!showTrainingOrderHistory)
                              }
                            >
                              {" "}
                              Collapse
                              <ExpandLessIcon />
                            </Button>
                          </div>
                        )}
                        {showTrainingOrderHistory && (
                          <TableContainer>
                            <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                  User
                                </TableCell>
                                <TableCell>
                                  Date
                                 </TableCell>
                                <TableCell>
                                  Field Name
                                </TableCell>
                                <TableCell>
                                  Description
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(trainingOrderHistory && trainingOrderHistory.data) ?
                              (
                                <>
                                  {trainingOrderHistory.data.map((row, index) => (
                                    <TableRow key={"trainingOrder" + index}>
                                      <TableCell className="centered">
                                        <Tooltip
                                          title={
                                            row?.actionByName ||
                                            row?.actionByEmail ||
                                            "Deleted user"
                                          }
                                          placement="right"
                                        >
                                          <Avatar
                                            sx={{
                                              bgcolor: colorFromName(
                                                row?.actionByName || ""
                                              ),
                                              height: 30,
                                              width: 30,
                                            }}
                                          >
                                            {!!row?.actionByName
                                              ? row?.actionByName.substring(0, 1)
                                              : undefined}
                                          </Avatar>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell className="centered">
                                        <Tooltip
                                          title={
                                            !!row.actionTimestamp
                                              ? new Date(row.actionTimestamp.toString()).toLocaleString()
                                              : ""
                                            }
                                        >
                                          <span>
                                            {!!row.actionTimestamp
                                              ? formatDateString(row.actionTimestamp?.toString())
                                              : ""}
                                          </span>
                                        </Tooltip>
                                        </TableCell>
                                        <TableCell className="centered">
                                          {row.fieldName === TrainingOrderFieldName.empty
                                            ? row.logType
                                            : row.logType +
                                              " - " +
                                              TrainingOrderFieldNameLabel[
                                                row.fieldName as TrainingOrderFieldName
                                              ]}
                                        </TableCell>
                                        <TableCell className="centered">
                                          {row.fieldName === TrainingOrderFieldName.empty
                                            ? row.newVal
                                            : row.oldVal + " -> " + row.newVal}
                                        </TableCell>
                                    </TableRow>
                                  ))}
                                </>
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={4}>
                                    <Box sx={{ display: "flex" }}>
                                      <CircularProgress />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </TableContainer>
                        )}
                      </div>
                    </Paper>
                  )}
                </div>
              </div>
            </main>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default TrainingOrderForm;
